import { useState, useEffect, useCallback, useRef } from 'react'
import { Howl } from 'howler'

import notificationSound from '@/assets/sounds/notifications.wav'

export interface NotificationSoundPreferences {
    enabled: boolean
    volume: number
    soundType: 'default'
}

const DEFAULT_PREFERENCES: NotificationSoundPreferences = {
    enabled: true,
    volume: 0.5,
    soundType: 'default',
}

const STORAGE_KEY = 'notificationSoundPrefs'
const STORAGE_EVENT = 'notificationSoundPrefsChanged'

export function useNotificationSound() {
    // Load preferences from localStorage
    const [preferences, setPreferences] = useState<NotificationSoundPreferences>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY)
            return stored ? JSON.parse(stored) : DEFAULT_PREFERENCES
        } catch {
            return DEFAULT_PREFERENCES
        }
    })

    // Create and manage Howler instance directly
    const howlerRef = useRef<Howl | null>(null)
    const [isPlaying, setIsPlaying] = useState(false)

    // Initialize Howler on mount
    useEffect(() => {
        howlerRef.current = new Howl({
            src: [notificationSound],
            volume: preferences.volume,
            onplay: () => setIsPlaying(true),
            onend: () => setIsPlaying(false),
            onstop: () => setIsPlaying(false),
        })

        return () => {
            howlerRef.current?.unload()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Update volume when preferences change
    useEffect(() => {
        if (howlerRef.current) {
            howlerRef.current.volume(preferences.volume)
        }
    }, [preferences.volume])

    // Sync preferences across all hook instances in the same window
    useEffect(() => {
        const handleStorageChange = () => {
            try {
                const stored = localStorage.getItem(STORAGE_KEY)
                if (stored) {
                    const newPrefs = JSON.parse(stored) as NotificationSoundPreferences
                    setPreferences(newPrefs)
                }
            } catch {
                // Failed to parse - ignore
            }
        }

        // Listen for custom event from other hook instances
        window.addEventListener(STORAGE_EVENT, handleStorageChange)

        return () => {
            window.removeEventListener(STORAGE_EVENT, handleStorageChange)
        }
    }, [])

    // Handle browser autoplay restrictions
    const [isAudioUnlocked, setIsAudioUnlocked] = useState(false)

    useEffect(() => {
        // Check if audio context is already unlocked
        const audioContext =
            typeof window !== 'undefined' &&
            (window.AudioContext ||
                (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)
        if (audioContext) {
            const ctx = new audioContext()
            if (ctx.state === 'running') {
                setIsAudioUnlocked(true)
                ctx.close()
            } else {
                ctx.close()
            }
        }

        // Unlock audio on first user interaction
        const unlockAudio = () => {
            setIsAudioUnlocked(true)
            // Remove both listeners once unlocked
            document.removeEventListener('click', unlockAudio)
            document.removeEventListener('keydown', unlockAudio)
        }

        document.addEventListener('click', unlockAudio)
        document.addEventListener('keydown', unlockAudio)

        return () => {
            document.removeEventListener('click', unlockAudio)
            document.removeEventListener('keydown', unlockAudio)
        }
    }, [])

    // Play function with proper checks
    const playNotification = useCallback(() => {
        // Browser will auto-unlock audio context on first user interaction (clicking Test button)
        // No need to pre-check isAudioUnlocked - let browser handle it naturally
        if (preferences.enabled && !isPlaying && howlerRef.current) {
            howlerRef.current.play()
        }
    }, [preferences.enabled, isPlaying])

    // Stop function
    const stop = useCallback(() => {
        if (howlerRef.current) {
            howlerRef.current.stop()
        }
    }, [])

    // Update preferences and persist to localStorage
    const updatePreferences = useCallback(
        (updates: Partial<NotificationSoundPreferences>) => {
            const newPrefs = { ...preferences, ...updates }
            setPreferences(newPrefs)
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs))
                // Notify other hook instances about the change
                window.dispatchEvent(new Event(STORAGE_EVENT))
            } catch {
                // Failed to save preferences - silent fail
            }
        },
        [preferences],
    )

    return {
        playNotification,
        stop,
        isPlaying,
        preferences,
        updatePreferences,
        isAudioUnlocked,
    }
}
